import {Button, IconButton, InputLabel, Stack} from '@mui/material';
import {ExecutionsGroupedAction, executionsService, ExecutionStatus} from '@store/operations/executions';
import {useSnackbar} from 'notistack';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {finalize} from 'rxjs';

import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import ModalComponent from '@components/modal/Modal.component';

import {AddQuantity, Remove} from '@icons/index';

import SelectComponent from '@components/inputs/select/Select.component';
import {Sizes} from '@constants/sizes.constants';
import dayjs from '@utils/dayjs.utils';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

interface ExecutionGroupedActionModalProps {
  handleClose: () => void;
}

const ExecutionGroupedActionModal = (props: ExecutionGroupedActionModalProps) => {
  const {handleClose} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ExecutionsGroupedAction>({});

  const updateData = (newData: ExecutionsGroupedAction) => setData((state) => ({...state, ...newData}));

  const handleSave = () => {
    setLoading(true);
    executionsService.executionsGroupedAction(data).pipe(finalize(() => setLoading(false))).subscribe({
      next: (ids) => {
        enqueueSnackbar(t('operations.executions.success.update', {count: ids.length}), {variant: 'success'});
        handleClose();
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleDelete = () => {
    setLoading(true);
    executionsService.deleteExecutions().pipe(finalize(() => setLoading(false))).subscribe({
      next: (ids) => {
        enqueueSnackbar(t('operations.executions.success.delete', {count: ids.length}), {variant: 'success'});
        handleClose();
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const errors = {
    executedAt: getFieldError(data.executedAt, FieldValidationType.DATE),
    plannedFrom: getFieldError(data.plannedFrom, FieldValidationType.DATE),
  };

  const disabled = !data.executedAt && data.quantity1 === undefined && !data.status && !data.plannedFrom;

  return (
    <ModalComponent
      title={t('global.groupedAction')}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <DatePickerComponent
            color="secondary"
            labelWidth={Sizes.datePickerWidth}
            label={t('operations.executions.manage.plannedFrom')}
            placeholder={t('global.selectDate')}
            handleChange={(plannedFrom) => updateData({plannedFrom: plannedFrom ? dayjs(plannedFrom).toDate() : undefined})}
            value={data.plannedFrom}
            error={errors.plannedFrom}/>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <InputLabel sx={{width: 160}}>
              {t('operations.executions.manage.containerQuantity')}
            </InputLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                disabled={(data.quantity1 || 0) <= 1}
                onClick={() => updateData({quantity1: (data.quantity1 || 0) - 1})}>
                <Remove/>
              </IconButton>
              <NumberTextFieldComponent
                inputWidth={80}
                centered
                pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
                handleChange={(value) => {
                  updateData({quantity1: value});
                }}
                value={data.quantity1}/>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                onClick={() => updateData({quantity1: (data.quantity1 || 0) + 1})}>
                <AddQuantity/>
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <InputLabel sx={{width: 160}}>
              {t('operations.executions.manage.wasteQuantity')}
            </InputLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                disabled={(data.quantity2 || 0) <= 1}
                onClick={() => updateData({quantity2: (data.quantity2 || 0) - 1})}>
                <Remove/>
              </IconButton>
              <NumberTextFieldComponent
                inputWidth={80}
                centered
                pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
                handleChange={(value) => {
                  updateData({quantity2: value});
                }}
                value={data.quantity2}/>
              <IconButton
                aria-label="quantity"
                centerRipple={false}
                onClick={() => updateData({quantity2: (data.quantity2 || 0) + 1})}>
                <AddQuantity/>
              </IconButton>
            </Stack>
          </Stack>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('operations.executions.manage.status')}
            placeholder={t('global.selectOption')}
            handleChange={(status) => updateData({status})}
            items={ExecutionStatus.selectItems()}
            value={data.status || ''}/>
        </Stack>
      )}
      actions={(
        <>
          <Button variant="contained" disabled={loading} onClick={handleDelete} color="error">
            {t('global.delete')}
          </Button>
          <Button
            variant="contained"
            disabled={loading || checkIfErrors(errors) || disabled}
            onClick={handleSave}
            color="primary">
            {t('global.save')}
          </Button>
        </>
      )}/>
  );
};

export default ExecutionGroupedActionModal;
